import type { ComponentChildren, JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { SHEET_CLOSE } from '../game/copy.ts';
import { Icon } from './Icon.tsx';

/**
 * D63. One frame for every sheet, so a new one cannot forget the way out. It draws the
 * dimmed backdrop, the panel, and a title row with the Close cross top right, and it
 * closes three ways: the cross, a tap on the dimmed area, and Escape. It traps nothing
 * else, so the screen behind keeps its own focus and scroll.
 *
 * D458: every sheet has a handle, a 20px title and a 44px Close (the cross), and nothing else to dismiss it. A
 * sheet opened from another sheet passes `onBack` and gets a 44px Back beside its title.
 *
 * Two shapes, both the same frame. `bottom` is the action sheet that rises from the foot
 * of the screen (sell, buy, place, move world); `centre` is the card that sits in the
 * middle (the Key, the jobs list, the away and stage-up summaries).
 */

/** Escape closes the sheet. Bound only while the sheet is open (D63). */
export function useCloseOnEscape(open: boolean, onClose: (() => void) | undefined): void {
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
}

/**
 * The Close control, top right of every sheet and of the tile panel: the same cross, the
 * same 44px target and the same aria-label, so one word closes everything.
 */
export function SheetClose({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={SHEET_CLOSE}
      style={{
        width: 44,
        height: 44,
        margin: -10,
        border: 0,
        background: 'transparent',
        color: 'var(--ink-2)',
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        flex: 'none',
      }}
    >
      <Icon name="x" size={18} />
    </button>
  );
}

/** D458: the Back control of a sheet opened from another sheet; 44px, left of the title. */
export const SHEET_BACK = 'Back';

export function SheetBack({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      aria-label={SHEET_BACK}
      style={{
        width: 44,
        height: 44,
        margin: '-10px 0 -10px -10px',
        border: 0,
        background: 'transparent',
        color: 'var(--ink-2)',
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        flex: 'none',
      }}
    >
      <Icon name="chevron-left" size={20} />
    </button>
  );
}

export interface SheetProps {
  /** False renders nothing, so a caller can hand the frame its own open flag. */
  open?: boolean;
  /** Where the panel sits. `bottom` rises from the foot of the screen. */
  align?: 'bottom' | 'centre';
  title: ComponentChildren;
  /** One line of secondary ink under the title. */
  subtitle?: ComponentChildren;
  /** An icon beside the title, the same way the tile panel heads a tile. */
  icon?: string;
  /** Anything that belongs in the title row to the left of Close, such as a duration. */
  titleAction?: ComponentChildren;
  /** The dialog's accessible name, when it should read differently from the title. */
  ariaLabel?: string;
  /** Kept from the sheets this frame replaced, for the screenshot and e2e scripts. */
  screenLabel?: string;
  testId?: string;
  onClose?: () => void;
  /** D458: a sheet opened from another sheet; draws Back, which returns to the one beneath. */
  onBack?: () => void;
  /** Above the tile panel and the map controls; a sheet over a sheet raises its own. */
  zIndex?: number;
  maxWidth?: number;
  /** Extra style for the panel itself. */
  style?: JSX.CSSProperties;
  children?: ComponentChildren;
}

export function Sheet({
  open = true,
  align = 'centre',
  title,
  subtitle,
  icon,
  titleAction,
  ariaLabel,
  screenLabel,
  testId,
  onClose,
  onBack,
  zIndex = 15,
  maxWidth,
  style,
  children,
}: SheetProps) {
  useCloseOnEscape(open, onClose);
  if (!open) return null;
  const bottom = align === 'bottom';
  const label = ariaLabel ?? (typeof title === 'string' ? title : undefined);
  return (
    <div
      onClick={onClose}
      {...(screenLabel ? { 'data-screen-label': screenLabel } : {})}
      {...(testId ? { 'data-testid': testId } : {})}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'var(--juice-scrim)',
        display: 'flex',
        alignItems: bottom ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: bottom ? 0 : 'var(--edge)',
        zIndex,
      }}
    >
      <div
        role="dialog"
        {...(label ? { 'aria-label': label } : {})}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: maxWidth ?? (bottom ? 520 : 420),
          maxHeight: bottom ? '92%' : '100%',
          overflowY: 'auto',
          background: 'var(--surface)',
          border: bottom ? 0 : '1px solid var(--line)',
          borderRadius: bottom ? '12px 12px 0 0' : 'var(--radius-md)',
          padding: bottom ? '16px var(--edge)' : 'var(--edge)',
          paddingBottom: bottom ? 'calc(16px + env(safe-area-inset-bottom))' : 'var(--edge)',
          display: 'grid',
          gap: bottom ? 14 : 8,
          alignContent: 'start',
          font: '400 14px/1.4 var(--font-ui)',
          color: 'var(--ink)',
          ...style,
        }}
      >
        <div
          aria-hidden
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: 'var(--line)',
            margin: bottom ? '-6px auto 0' : '-2px auto 0',
          }}
        />
        <header style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 28 }}>
          {onBack && <SheetBack onBack={onBack} />}
          {icon && <Icon name={icon} size={22} style={{ color: 'var(--ink-2)' }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* D104: a sheet's title is a heading, so the page under it keeps its own. */}
            <h2 style={{ margin: 0, font: '600 var(--fs-title)/1.3 var(--font-ui)' }}>{title}</h2>
            {subtitle && (
              <div
                style={{ font: '400 var(--fs-caption)/1.4 var(--font-ui)', color: 'var(--ink-2)' }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {titleAction}
          {onClose && <SheetClose onClose={onClose} />}
        </header>
        {children}
      </div>
    </div>
  );
}
